<template>
  <v-text-field v-if="!readOnly && !field.readOnly"
                ref="inputRef"
                v-model="postalCode"
                v-mask="'#####-###'"
                return-masked-value
                :loading="loading"
                :clearable="field.clearable"
                :hint="field.hint"
                :persistent-hint="true"
                :required="maybeCall(field.required, value)"
                :disabled="field.disabled"
                :rules="validationRules"
                :label="label"
                :class="field.class"
                :prepend-icon="field.prependIcon"
                :filled="box || field.box"
                autocomplete="off"
                @blur="findPostalCode"
                @keypress.enter="findPostalCode">
    <template #append>
      <v-icon @click="findPostalCode">
        mdi-magnify
      </v-icon>
    </template>
  </v-text-field>
  <div v-else :class="{'reg-information': !small, 'reg-information-small': small}">
    <label>{{ field.label }}</label>
    <p>{{ formattedValue || '-' }}</p>
  </div>
</template>

<script>
import { defineComponent, ref, watch, getCurrentInstance, computed, toRef } from 'vue'
import { useDynamicField } from '../composables/useDynamicField'
import { useFieldFormatting } from '../composables/formatting/useFieldFormatting'
import axios from 'axios'
import CONFIG from '~config/env'

export default defineComponent({
  name: 'DynamicPostalCode',
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    field: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    isValidating: {
      type: Boolean,
      default: false
    },
    itemKey: {
      type: String,
      default: ''
    }
  },
  emits: ['input', 'change'],
  setup(props, { emit, expose }) {
    const instance = getCurrentInstance()
    const $toasted = instance.proxy.$toasted

    const {
      fieldValue,
      validationRules,
      label,
      inputRef,
      maybeCall,
      setFieldValue,
      hasError
    } = useDynamicField({
      value: toRef(props, 'value'),
      field: props.field,
      readOnly: props.readOnly,
      isValidating: toRef(props, 'isValidating'),
      itemKey: props.itemKey,
      emptyValue: undefined,
      emit
    })

    const postalCode = ref('')
    const loading = ref(false)
    
    const formattedValue = useFieldFormatting({
      model: computed(() => props.value),
      field: computed(() => props.field)
    })

    const findPostalCode = () => {
      const postalCodeValue = postalCode.value
      if (!postalCodeValue) {
        return
      }
      if (postalCodeValue.length !== 9) {
        $toasted.error('CEP inválido.')
        return
      }
      loading.value = true
      
      axios.get(`${CONFIG.VIA_CEP_URL}/${postalCodeValue}/json/`)
        .then(response => {
          const address = response.data
          if (address.erro) {
            $toasted.error('CEP não encontrado.')
            return
          }

          setFieldValue({
            postalCode: address.cep,
            street: address.logradouro,
            neighborhood: address.bairro,
            city: address.localidade,
            state: address.estado,
            country: 'Brasil',
            ibgeCityCode: address.ibge
          })
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          loading.value = false
        })
    }

    const onFieldValueChange = (value) => {
      if (value && typeof value === 'object') {
        postalCode.value = value.postalCode || ''
        return
      }
      postalCode.value = value || ''
    }

    watch(fieldValue, onFieldValueChange, { immediate: true })

    // Expose properties for DynamicForm validation
    expose({
      hasError,
      validate: () => inputRef.value?.validate?.()
    })

    return {
      postalCode,
      loading,
      validationRules,
      label,
      inputRef,
      maybeCall,
      findPostalCode,
      formattedValue
    }
  }
})
</script>

<style lang="scss">
</style>